import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, FileText } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const SLAPTears = () => {
  const { theme } = useSite();
  
  const faqs = [
    { question: 'What is a SLAP tear?', answer: 'SLAP stands for Superior Labrum Anterior to Posterior. It is a tear of the top part of the labrum (cartilage rim) where the biceps tendon attaches. Common in overhead athletes and can cause deep shoulder pain, clicking, and weakness.' },
    { question: 'How is a SLAP tear diagnosed?', answer: 'Diagnosis involves clinical examination (O\'Brien\'s test, Speed\'s test) and MRI arthrogram (gold standard). Plain MRI may miss subtle tears. The combination of clinical findings and imaging guides treatment.' },
    { question: 'Do all SLAP tears need surgery?', answer: 'No. Many SLAP tears, especially in patients over 40 or Type I (fraying) lesions, respond well to conservative treatment with physiotherapy. Surgery is reserved for symptomatic Type II-IV tears in active patients who fail conservative care.' },
    { question: 'What are the treatment options?', answer: 'Conservative: Activity modification, physiotherapy focusing on scapular stability and rotator cuff strengthening. Surgical: Arthroscopic SLAP repair (younger patients), biceps tenodesis (patients over 35-40), or debridement for degenerative tears.' },
    { question: 'What is recovery like after SLAP repair?', answer: 'Post-operative protocol: Sling for 4-6 weeks, no active biceps use for 6 weeks, progressive motion and strengthening over 3-6 months. Return to overhead sports typically 6-9 months. Outcomes are good but return to pre-injury throwing level can be challenging.' }
  ];

  return (
    <div className="min-h-screen" data-testid="slap-tears-page">
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Instability & Sports
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">SLAP Tear Treatment in Hyderabad</h1>
            <p className="text-xl text-gray-600">Superior labral repair & biceps tenodesis by Dr. Deepthi Nandan Reddy at Apollo Hospitals</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding SLAP Tears</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>SLAP lesions involve the superior labrum where the biceps tendon anchors to the glenoid. They commonly occur in overhead athletes (baseball, tennis, volleyball) due to repetitive stress, or from acute trauma like falls or traction injuries.</p>
              <p><strong>Snyder Classification:</strong></p>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <Card><CardHeader><CardTitle className="text-lg">Type I</CardTitle></CardHeader><CardContent className="text-sm text-gray-600">Labral fraying, biceps anchor intact. Most common. Usually managed conservatively.</CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg">Type II</CardTitle></CardHeader><CardContent className="text-sm text-gray-600">Biceps anchor detached from glenoid. Most surgically significant. Requires repair in symptomatic patients.</CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg">Type III</CardTitle></CardHeader><CardContent className="text-sm text-gray-600">Bucket-handle tear, biceps intact. Can cause mechanical symptoms. Excision of unstable fragment.</CardContent></Card>
              <Card><CardHeader><CardTitle className="text-lg">Type IV</CardTitle></CardHeader><CardContent className="text-sm text-gray-600">Bucket-handle tear extending into biceps. May need repair or tenodesis depending on biceps involvement.</CardContent></Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment Algorithm</h2>
            </div>
            <div className="space-y-4">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle>Conservative First (3-6 months)</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>• Activity modification (avoid aggravating movements)</p>
                  <p>• Scapular stabilization exercises</p>
                  <p>• Rotator cuff and core strengthening</p>
                  <p>• Posterior capsule stretching</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader><CardTitle>Surgical Options (If Conservative Fails)</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p><strong>Age &lt;35, Active Overhead Athlete:</strong> Arthroscopic SLAP repair with suture anchors</p>
                  <p><strong>Age &gt;35-40 or Low-Demand:</strong> Biceps tenodesis (excellent results, more predictable)</p>
                  <p><strong>Degenerative/Type I:</strong> Debridement alone</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 p-6 rounded-lg text-sm text-gray-600 space-y-2">
              <p>1. Snyder SJ, et al. SLAP lesions of the shoulder. Arthroscopy. 1990</p>
              <p>2. Boileau P, et al. Arthroscopic treatment of isolated type II SLAP lesions: biceps tenodesis as an alternative to reinsertion. AJSM. 2009</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default SLAPTears;
